// @src/pre-deployment/src/components/ChapterReview.js v1.0
// Review system that uses tracking data to generate personalized review exercises
// Based on user's knowledge profile from tracking.js

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import { useAuth } from '../../contexts/AuthContext';
import { useKnowledgeProfile, ChapterProgress } from '../../data/tracking';
import { useVocabulary, useGrammarPoints } from '../../data/useDataService';

const ChapterReview = () => {
    const { level, chapter } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [reviewState, setReviewState] = useState('loading'); // loading, setup, reviewing, completed
    const [reviewExercises, setReviewExercises] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [score, setScore] = useState({ correct: 0, total: 0 });
    const [selectedAnswer, setSelectedAnswer] = useState('');
    const [showResult, setShowResult] = useState(false);
    const [isCorrect, setIsCorrect] = useState(false);
    
    // Get user's knowledge profile
    const { profile, loading: profileLoading } = useKnowledgeProfile(user?.uid, level, chapter, 'Learn');
    
    // Get content for review generation
    const { data: vocabulary } = useVocabulary(level, chapter);
    const { data: grammarPoints } = useGrammarPoints(level, chapter);
    
    // Initialize review module tracking on mount
    useEffect(() => {
        if (user?.uid && level && chapter) {
            console.log('🔍 ChapterReview: Initializing review module tracking');
            ChapterProgress.initialize(user.uid, level, chapter);
            ChapterProgress.updateModule(user.uid, level, chapter, 'Review', {
                user_started: true,
                start_date: new Date().toISOString(),
                last_opened: new Date().toISOString(),
                interaction_type: 'template-based review'
            });
        }
    }, [user?.uid, level, chapter]);
    
    const shuffleArray = (array) => {
        const newArray = [...array];
        for (let i = newArray.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
        }
        return newArray;
    };

    const generateVocabOptions = useCallback((correctWord, allVocab) => {
        const options = [correctWord.translations?.english || correctWord.english];
        const others = allVocab.filter(w => w.id !== correctWord.id);
        
        for (let i = 0; i < 3 && i < others.length; i++) {
            const randomWord = others[Math.floor(Math.random() * others.length)];
            options.push(randomWord.translations?.english || randomWord.english);
        }
        
        return shuffleArray(options);
    }, []);
    
    const _generateReviewExercises = useCallback(() => {
        console.log('📝 Generating review exercises from knowledge profile');
        const exercises = [];
        
        // VOCABULARY REVIEW: Items with incorrect attempts (HIGHEST PRIORITY)
        if (profile.vocabulary?.has_incorrect && vocabulary) {
            profile.vocabulary.has_incorrect.forEach(wordId => {
                const word = vocabulary.find(w => w.id === wordId);
                if (word) {
                    exercises.push({
                        type: 'vocabulary',
                        content_id: wordId,
                        question: `What does "${word.korean}" mean?`,
                        correct_answer: word.translations?.english || word.english,
                        options: generateVocabOptions(word, vocabulary),
                        korean: word.korean,
                        english: word.translations?.english || word.english,
                        priority: 'has_incorrect'
                    });
                }
            });
        }
        
        // VOCABULARY REVIEW: Exposed-only words (MEDIUM PRIORITY)
        if (profile.vocabulary?.exposed_only && vocabulary) {
            profile.vocabulary.exposed_only.forEach(wordId => {
                const word = vocabulary.find(w => w.id === wordId);
                if (word) {
                    exercises.push({
                        type: 'vocabulary',
                        content_id: wordId,
                        question: `What does "${word.korean}" mean?`,
                        correct_answer: word.translations?.english || word.english,
                        options: generateVocabOptions(word, vocabulary),
                        korean: word.korean,
                        english: word.translations?.english || word.english,
                        priority: 'exposed_only'
                    });
                }
            });
        }
        
        // GRAMMAR REVIEW: Items with incorrect attempts (HIGHEST PRIORITY)
        if (profile.grammar?.has_incorrect && grammarPoints) {
            profile.grammar.has_incorrect.forEach(grammarId => {
                const grammar = grammarPoints.find(g => g.id === grammarId);
                if (grammar && grammar.examples && grammar.examples.length > 0) {
                    const example = grammar.examples[0];
                    exercises.push({
                        type: 'grammar',
                        content_id: grammarId,
                        question: grammar.summary_en || grammar.title,
                        correct_answer: example.korean || grammar.korean_example,
                        options: [example.korean, `${example.korean} (wrong)`, `Not ${example.korean}`, 'None'],
                        grammar_title: grammar.title_kr || grammar.title,
                        priority: 'has_incorrect'
                    });
                }
            });
        }
        
        // GRAMMAR REVIEW: Exposed-only grammar (MEDIUM PRIORITY)
        if (profile.grammar?.exposed_only && grammarPoints) {
            profile.grammar.exposed_only.forEach(grammarId => {
                const grammar = grammarPoints.find(g => g.id === grammarId);
                if (grammar && grammar.examples && grammar.examples.length > 0) {
                    const example = grammar.examples[0];
                    exercises.push({
                        type: 'grammar',
                        content_id: grammarId,
                        question: grammar.summary_en || grammar.title,
                        correct_answer: example.korean || grammar.korean_example,
                        options: [example.korean, `${example.korean} (wrong)`, `Not ${example.korean}`, 'None'],
                        grammar_title: grammar.title_kr || grammar.title,
                        priority: 'exposed_only'
                    });
                }
            });
        }
        
        // Sort by priority: items with incorrect attempts first, then exposed-only
        exercises.sort((a, b) => {
            if (a.priority === 'has_incorrect' && b.priority !== 'has_incorrect') return -1;
            if (a.priority !== 'has_incorrect' && b.priority === 'has_incorrect') return 1;
            return 0;
        });
        
        console.log(`✅ Generated ${exercises.length} review exercises`);
        console.log(`   - Has Incorrect: ${exercises.filter(e => e.priority === 'has_incorrect').length}`);
        console.log(`   - Exposed Only: ${exercises.filter(e => e.priority === 'exposed_only').length}`);
        setReviewExercises(exercises);
        setReviewState(exercises.length > 0 ? 'setup' : 'completed');
    }, [profile, vocabulary, grammarPoints, generateVocabOptions]);

    const generateReviewExercises = _generateReviewExercises; // eslint-disable-line no-unused-vars
    
    const startReview = () => {
        setReviewState('reviewing');
        setCurrentIndex(0);
        setScore({ correct: 0, total: 0 });
    };
    
    const handleAnswerSelect = (answer) => {
        if (!showResult) setSelectedAnswer(answer);
    };
    
    const handleSubmit = () => {
        if (!selectedAnswer || showResult) return;
        
        const currentExercise = reviewExercises[currentIndex];
        const correct = selectedAnswer === currentExercise.correct_answer;
        
        setIsCorrect(correct);
        setShowResult(true);
        setScore(prev => ({
            correct: prev.correct + (correct ? 1 : 0),
            total: prev.total + 1
        }));
        
        // Track review attempt
        if (user?.uid) {
            ChapterProgress.addQuestion(
                user.uid,
                level,
                chapter,
                'Review',
                currentIndex,
                {
                    content_type: currentExercise.type,
                    content_id: currentExercise.content_id,
                    user_choice: selectedAnswer,
                    correct_choice: currentExercise.correct_answer,
                    user_isCorrect: correct
                }
            );
        }
        
        setTimeout(() => {
            if (currentIndex < reviewExercises.length - 1) {
                setCurrentIndex(prev => prev + 1);
                setSelectedAnswer('');
                setShowResult(false);
            } else {
                setReviewState('completed');
                
                // Mark review module as complete
                if (user?.uid) {
                    ChapterProgress.updateModule(user.uid, level, chapter, 'Review', {
                        completion: true,
                        completion_time: new Date().toISOString(),
                        last_question_completed: reviewExercises.length
                    });
                }
            }
        }, 1500);
    };
    
    if (reviewState === 'loading' || profileLoading) {
        return (
            <div className="module-container">
                <div className="exercise-top-navigation">
                    <div className="module-info">
                        <span>Level {level}</span>
                        <span className="separator">•</span>
                        <span>Chapter {chapter}</span>
                        <span className="separator">•</span>
                        <span>Loading Review...</span>
                    </div>
                </div>
                <div className="vocabulary-content">
                    <p>Loading review exercises...</p>
                </div>
            </div>
        );
    }
    
    if (reviewState === 'setup') {
        return (
            <div className="module-container">
                <div className="exercise-top-navigation">
                    <div className="module-info">
                        <span>Level {level}</span>
                        <span className="separator">•</span>
                        <span>Chapter {chapter}</span>
                        <span className="separator">•</span>
                        <span>Review</span>
                    </div>
                    <div className="module-nav-buttons">
                        <button className="module-nav-btn" onClick={() => navigate('/app-homepage')}>
                            <i className="fas fa-home"></i>Menu
                        </button>
                    </div>
                </div>
                <div className="vocabulary-content">
                    <div className="setup-screen">
                        <h2>Chapter Review</h2>
                        <p>Review exercises based on your performance</p>
                        
                        <div className="vocabulary-preview">
                            <h3>Review Summary</h3>
                            <div className="vocab-sample">
                                <ul>
                                    <li>Total Exercises: {reviewExercises.length}</li>
                                    <li>Vocabulary: {reviewExercises.filter(e => e.type === 'vocabulary').length}
                                        <ul style={{ fontSize: '0.9em', marginTop: '0.5rem' }}>
                                            <li>✗ Needs Review: {reviewExercises.filter(e => e.type === 'vocabulary' && e.priority === 'has_incorrect').length}</li>
                                            <li>Additional Practice: {reviewExercises.filter(e => e.type === 'vocabulary' && e.priority === 'exposed_only').length}</li>
                                        </ul>
                                    </li>
                                    <li>Grammar: {reviewExercises.filter(e => e.type === 'grammar').length}
                                        <ul style={{ fontSize: '0.9em', marginTop: '0.5rem' }}>
                                            <li>✗ Needs Review: {reviewExercises.filter(e => e.type === 'grammar' && e.priority === 'has_incorrect').length}</li>
                                            <li>Additional Practice: {reviewExercises.filter(e => e.type === 'grammar' && e.priority === 'exposed_only').length}</li>
                                        </ul>
                                    </li>
                                </ul>
                            </div>
                        </div>
                        
                        <button className="start-button" onClick={startReview}>
                            Start Review
                        </button>
                    </div>
                </div>
            </div>
        );
    }
    
    if (reviewState === 'reviewing') {
        const currentExercise = reviewExercises[currentIndex];
        
        return (
            <div className="module-container">
                <div className="exercise-top-navigation">
                    <div className="module-info">
                        <span>Level {level}</span>
                        <span className="separator">•</span>
                        <span>Chapter {chapter}</span>
                        <span className="separator">•</span>
                        <span>Review {currentIndex + 1}/{reviewExercises.length}</span>
                    </div>
                    <div className="module-nav-buttons">
                        <button className="module-nav-btn" onClick={() => navigate('/app-homepage')}>
                            <i className="fas fa-home"></i>Menu
                        </button>
                    </div>
                </div>
                <div className="vocabulary-content">
                    <div className="game-header">
                        <div className="score-display">
                            Score: {score.correct}/{score.total}
                            {score.total > 0 && (
                                <span className="accuracy">
                                    ({((score.correct / score.total) * 100).toFixed(1)}%)
                                </span>
                            )}
                        </div>
                        <div className="progress-bar">
                            <div className="progress-fill" style={{ width: `${((currentIndex + 1) / reviewExercises.length) * 100}%` }}></div>
                        </div>
                    </div>
                    
                    <div className="question-area">
                        <div className="question-header">
                            <h3>Question {currentIndex + 1}</h3>
                        </div>
                        <div className="source-word">{currentExercise.question}</div>
                        
                        <div className="options-container">
                            {currentExercise.options.map((option, index) => (
                                <button
                                    key={index}
                                    onClick={() => handleAnswerSelect(option)}
                                    disabled={showResult}
                                    className={`option-button ${
                                        selectedAnswer === option ? 'selected' : ''
                                    } ${
                                        showResult && option === currentExercise.correct_answer ? 'correct' : ''
                                    } ${
                                        showResult && selectedAnswer === option && option !== currentExercise.correct_answer ? 'incorrect' : ''
                                    }`}
                                >
                                    {option}
                                </button>
                            ))}
                        </div>
                        
                        {!showResult && (
                            <button className="submit-button" onClick={handleSubmit} disabled={!selectedAnswer}>
                                Submit
                            </button>
                        )}
                        
                        {showResult && (
                            <div className="result-display">
                                {isCorrect ? (
                                    <div className="correct-result">✓ Correct!</div>
                                ) : (
                                    <div className="incorrect-result">
                                        ✗ Incorrect. Correct answer: {currentExercise.correct_answer}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }
    
    if (reviewState === 'completed') {
        return (
            <div className="module-container">
                <div className="exercise-top-navigation">
                    <div className="module-info">
                        <span>Level {level}</span>
                        <span className="separator">•</span>
                        <span>Chapter {chapter}</span>
                        <span className="separator">•</span>
                        <span>Review Complete</span>
                    </div>
                </div>
                <div className="vocabulary-content">
                    <div className="completion-screen">
                        <h2>Review Complete!</h2>
                        {reviewExercises.length > 0 ? (
                            <div className="final-results">
                                <div className="result-card">
                                    <h3>Final Score</h3>
                                    <div className="score-large">{score.correct} / {score.total}</div>
                                    <div className="accuracy-large">
                                        {((score.correct / score.total) * 100).toFixed(1)}% Accuracy
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <p>No review exercises available. Great job - everything is mastered!</p>
                        )}
                        
                        <div className="completion-actions">
                            <button className="action-btn primary" onClick={() => navigate('/app-homepage')}>
                                <i className="fas fa-home"></i>
                                Return to Menu
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
    
    return null;
};

export default ChapterReview;

